import React from 'react';
import BlogLandingPage, { BlogConfig } from '../../components/BlogLandingPage';
import iphoneBatteryAd from '../../assets/blogs/iphoneBatteryAd.jpeg';

const config: BlogConfig = {
  slug: 'iphone-13-battery-replacement',
  heroImage: iphoneBatteryAd,
  title: 'iPhone 13 Battery Replacement — 180-Day Warranty, Doorstep Service',
  subtitle: 'Is your iPhone 13 draining fast or shutting down unexpectedly? Get a genuine battery replaced at your home or office in Mumbai — done in under an hour.',
  heroLabel: 'iPhone 13 Battery Repair',
  intro:
    'The iPhone 13 houses a 3,227 mAh battery designed to last a full day under normal use. After 12–18 months of charging cycles, most batteries begin to degrade — holding less charge, throttling performance, and shutting off at 20–30%. A battery replacement restores your iPhone 13 to factory-level performance without buying a new phone. minto sends a certified technician to your door anywhere in Mumbai. The entire replacement takes under 45 minutes, and your battery is backed by our industry-leading 180-day warranty.',
  warrantyNote:
    'Every battery replaced by minto comes with a 180-day warranty. If you experience capacity loss, unexpected shutdowns, swelling, or charging issues within 180 days of replacement — we fix it free of charge. Water damage and physical breakage are not covered under warranty.',
  signs: [
    'iPhone 13 battery drains from 100% to 0% within 4–5 hours of light use',
    'Phone shuts down suddenly at 15–30% battery remaining',
    'Device gets unusually warm while charging or during calls',
    'Battery percentage jumps or drops erratically',
    'iPhone 13 takes 3+ hours to charge fully',
    'Performance feels sluggish, apps lag or freeze',
    'Battery health below 80% in Settings → Battery → Battery Health',
  ],
  sections: [
    {
      heading: 'What Causes iPhone 13 Battery Degradation?',
      body: 'Lithium-ion batteries have a finite number of charge cycles — typically 500 full cycles before noticeable capacity loss. Frequent overnight charging, heavy gaming, leaving the iPhone 13 in extreme heat or cold, and using fast chargers constantly all accelerate this degradation. The iPhone 13\'s battery management system will begin throttling CPU speed once battery health drops below 80% to prevent unexpected shutdowns — which is why the phone feels slower even though the hardware is unchanged.',
      bullets: [
        'Charging past 80% repeatedly stresses battery cells',
        'Heat above 35°C permanently reduces battery capacity',
        'Deep discharge (letting it hit 0%) shortens cycle life',
        'Third-party chargers without proper voltage regulation cause wear',
      ],
    },
    {
      heading: 'Why Doorstep iPhone 13 Battery Replacement Makes Sense',
      body: 'Taking your iPhone 13 to a repair shop means handing over your device for hours, sometimes overnight. With minto, a background-verified technician comes to your home or office. The iPhone 13 battery swap takes about 40–45 minutes and your phone never leaves your sight. We bring all tools and parts — you just need to be available.',
      bullets: [
        'No travel, no waiting in queue',
        'Repair done in your presence — full transparency',
        'OEM-grade battery cells, tested before and after install',
        '180-day warranty issued with every replacement',
        'Available 7 days a week, 8 AM to 8 PM across Mumbai',
      ],
    },
    {
      heading: 'What Our Technicians Check During the iPhone 13 Repair',
      body: 'A battery replacement is not just a swap. Our technicians run a full diagnostic before and after the job to ensure the replacement resolves your issue and nothing else is missed.',
      bullets: [
        'Pre-repair battery health check and cycle count reading',
        'Charging port and cable inspection',
        'Thermal test after new battery installation',
        'Capacity and voltage verification post-install',
        'Software reset to recalibrate battery indicator',
      ],
    },
  ],
  faqs: [
    {
      q: 'How long does iPhone 13 battery replacement take?',
      a: 'The replacement takes 40–45 minutes at your location. Our technician carries all tools and the replacement battery — no prior drop-off needed.',
    },
    {
      q: 'What warranty do I get on the iPhone 13 battery?',
      a: 'You get a 180-day warranty on the battery. If you face capacity issues, unexpected shutdowns, swelling, or charging problems within 180 days, we fix it free. Note: water damage and physical breakage are not covered.',
    },
    {
      q: 'Will my iPhone 13 data be safe during battery replacement?',
      a: 'Yes. Battery replacement does not affect your data. The technician only opens the rear panel — your storage and software are completely untouched.',
    },
    {
      q: 'How do I know if my iPhone 13 battery needs replacement?',
      a: 'Go to Settings → Battery → Battery Health & Charging. If Maximum Capacity is below 80%, replacement is recommended. You can also book a free diagnosis with minto via WhatsApp.',
    },
    {
      q: 'Do you use original iPhone 13 batteries?',
      a: 'We use OEM-grade batteries that match original capacity, voltage, and form factor specifications. These perform identically to factory batteries and are covered by our 180-day warranty.',
    },
    {
      q: 'Is doorstep iPhone 13 repair available on weekends?',
      a: 'Yes, we operate 7 days a week from 8 AM to 8 PM across all major Mumbai locations including Andheri, Bandra, Borivali, Thane, and Navi Mumbai.',
    },
  ],
  seo: {
    title: 'iPhone 13 Battery Replacement Mumbai | 180-Day Warranty | minto Repairs',
    description: 'Get your iPhone 13 battery replaced at your doorstep in Mumbai. Certified technicians, OEM-grade battery, 180-day warranty. Fast drain or sudden shutdowns? Book now. Independent service — not affiliated with Apple Inc.',
    keywords: 'iphone 13 battery replacement, iphone 13 battery repair mumbai, doorstep battery replacement iphone 13, iphone 13 battery draining fast, iphone 13 sudden shutdown fix, iphone 13 battery health, mobile battery repair mumbai, minto repairs battery',
  },
};

const iPhone13BatteryPage: React.FC = () => <BlogLandingPage blog={config} />;
export default iPhone13BatteryPage;
